import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Copy, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../api/client';
import { useChargerSecurity, type Charger } from '../../api/chargers';
import { FormSection } from '../../components/form/FieldRow';
import { useToast } from '../../components/Toast';
import { colours, radius } from '../../theme';
import { useUnsavedChanges } from '../../components/unsaved/unsaved';

const PROFILES = [
  { value: 0, title: 'No password', text: 'Any device that knows the charge point ID can connect as this charger. Only while moving chargers over.' },
  { value: 1, title: 'Password', text: 'The charger signs in with a password, over a plain connection (ws://). Fine on a private network.' },
  { value: 2, title: 'Password over an encrypted connection', text: 'Password and wss:// (TLS). The target for every charger on the internet.' },
] as const;

/** 24 letters and numbers: well inside the 16 to 40 characters OCPP allows. */
function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint32Array(24));
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
}

/** OCPP security profile and the charger's password (admins). */
export function SecurityTab({ charger: c }: { charger: Charger }) {
  const save = useChargerSecurity();
  const toast = useToast();
  const [profile, setProfile] = useState<0 | 1 | 2>(c.securityProfile as 0 | 1 | 2);
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'send' | 'already'>(c.isConnected ? 'send' : 'already');
  const [error, setError] = useState('');
  const needsPassword = profile > 0 && !c.hasPassword;
  const pwError = password && (password.length < 16 || password.length > 40 || /\s/.test(password)) ? '16 to 40 letters, numbers or symbols, no spaces' : '';
  const changed = profile !== c.securityProfile || !!password;

  const submit = async (): Promise<boolean> => {
    setError('');
    if (needsPassword && !password) {
      setError('Choose a password for this profile.');
      return false;
    }
    if (pwError) return false;
    try {
      await save.mutateAsync({ id: c.id, securityProfile: profile, ...(password ? { password } : {}), sendToCharger: mode === 'send' });
      setPassword('');
      toast(password && mode === 'send' ? 'The charger accepted its new password. It reconnects with it.' : 'Security settings were saved.');
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? (e.fields.password ?? e.message) : 'Something went wrong.');
      return false;
    }
  };
  useUnsavedChanges({
    dirty: changed,
    save: submit,
    discard: () => {
      setProfile(c.securityProfile as 0 | 1 | 2);
      setPassword('');
    },
    tab: true,
  });

  return (
    <Box sx={{ display: 'grid', gap: 4, maxWidth: 640 }}>
      <FormSection title="Security profile">
        <RadioGroup value={profile} onChange={(e) => setProfile(Number(e.target.value) as 0 | 1 | 2)} sx={{ gap: 1 }}>
          {PROFILES.map((p) => (
            <Box
              key={p.value}
              component="label"
              sx={{ display: 'flex', gap: 1, p: 1.5, border: `1px solid ${profile === p.value ? colours.nearBlack : colours.hairline}`, borderRadius: `${radius.panel}px`, cursor: 'pointer' }}
            >
              <Radio value={p.value} sx={{ p: 0.5 }} slotProps={{ input: { 'aria-label': p.title, 'aria-describedby': `profile-${p.value}` } }} />
              <Box>
                <Typography sx={{ fontWeight: 500 }} aria-hidden>{p.title}</Typography>
                <Typography variant="body2" color="textSecondary" id={`profile-${p.value}`}>{p.text}</Typography>
              </Box>
            </Box>
          ))}
        </RadioGroup>
      </FormSection>

      <FormSection title={c.hasPassword ? 'Change the password' : 'Password'}>
        <Typography color="textSecondary" sx={{ mt: -1 }}>
          {c.hasPassword ? 'A password is set. Leave this empty to keep it.' : 'No password is set yet.'} Pulsepoint keeps only a scrambled copy, so write it down if the installer needs it.
        </Typography>
        <TextField
          label="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!pwError}
          helperText={pwError || 'Use Generate for a strong one'}
          autoComplete="off"
          slotProps={{
            htmlInput: { spellCheck: false, className: 'num' },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {password && (
                    <Tooltip title="Copy">
                      <IconButton aria-label="Copy the password" onClick={() => void navigator.clipboard?.writeText(password)} size="small">
                        <Copy size={18} strokeWidth={1.5} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Generate">
                    <IconButton aria-label="Generate a password" onClick={() => setPassword(generatePassword())} size="small">
                      <RefreshCw size={18} strokeWidth={1.5} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            },
          }}
        />
        {password && (
          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as 'send' | 'already')}>
            <FormControlLabel value="send" control={<Radio />} disabled={!c.isConnected} label={`Send it to the charger now${c.isConnected ? '' : ' (it is offline)'}`} />
            <FormControlLabel value="already" control={<Radio />} label="It is already set on the charger (e.g. by the installer)" />
          </RadioGroup>
        )}
      </FormSection>

      {profile === 2 && (
        <Alert severity="info" icon={false} sx={{ color: 'text.secondary' }}>
          The charger also needs the address wss://ocpp.infinityev.com.au in its own settings.
        </Alert>
      )}
      {error && <Alert severity="error" role="alert">{error}</Alert>}
      <Box>
        <Button variant="contained" onClick={() => void submit()} disabled={save.isPending || !changed || !!pwError}>
          {save.isPending ? (mode === 'send' && password ? 'Waiting for the charger…' : 'Saving…') : 'Save security'}
        </Button>
      </Box>
    </Box>
  );
}
